import { useEffect, useRef, useState } from "react";
import Reveal from "./Reveal";

const CARDS = [
    {
        title: "Vende sin inventario por adelantado",
        desc: "Tu proveedor despacha directo. Tú ganas el margen sin tocar un producto.",
        img: "/diferencia-1.webp",
    },
    {
        title: "Diseñado para RD y LATAM",
        desc: "Logística real por país, transportadoras que funcionan, scripts probados en tu mercado.",
        img: "/diferencia-2.webp",
    },
    {
        title: "Ads que venden — no que gastan",
        desc: "Facebook y TikTok Ads con testeo desde $20/día. Sabes en 48h si un producto tiene potencial.",
        img: "/diferencia-3.webp",
    },
    {
        title: "Acompañamiento real hasta que vendas",
        desc: "Llamadas en vivo conmigo todos los lunes y jueves. Soporte del equipo. No te dejamos tirado.",
        img: "/diferencia-4.webp",
    },
];

const STATS = [
    { target: 14, suffix: "", label: "Módulos" },
    { target: 100, suffix: "+", label: "Clases" },
    { target: 50, suffix: "+", label: "Entregables" },
    { target: 5, suffix: "", label: "Países LATAM" },
];

function CountUp({ target, suffix }) {
    const ref = useRef(null);
    const [val, setVal] = useState(0);

    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const io = new IntersectionObserver(
            ([entry]) => {
                if (!entry.isIntersecting) return;
                io.disconnect();
                const t0 = performance.now();
                const dur = 1500;
                const step = (now) => {
                    const p = Math.min(1, (now - t0) / dur);
                    setVal(Math.round(target * (1 - Math.pow(1 - p, 3))));
                    if (p < 1) requestAnimationFrame(step);
                };
                requestAnimationFrame(step);
            },
            { threshold: 0.4 },
        );
        io.observe(el);
        return () => io.disconnect();
    }, [target]);

    return (
        <span ref={ref}>
            {val}
            {suffix}
        </span>
    );
}

export default function Diferencia() {
    return (
        <>
            <section className="relative overflow-hidden px-5 py-20 sm:py-28" data-testid="pain-section">
                <div className="pointer-events-none absolute -right-40 top-0 h-72 w-72 rounded-full bg-[#ff5c00]/[0.05] blur-[100px]" />
                <div className="relative mx-auto max-w-3xl">
                    <Reveal>
                        <div className="mb-4 flex items-center gap-2">
                            <span className="h-1.5 w-1.5 rounded-full bg-[#ef4444]" />
                            <span className="text-xs font-medium uppercase tracking-[0.2em] text-[#888]">
                                Por qué no ha funcionado antes
                            </span>
                        </div>
                        <h2 className="font-display text-3xl font-extrabold leading-[1.08] sm:text-5xl">
                            Llevas años trabajando. Y el dinero{" "}
                            <em className="serif-accent text-grad">nunca alcanza.</em>
                        </h2>
                    </Reveal>

                    <Reveal delay={120}>
                        <p className="mt-8 text-sm leading-relaxed text-[#999] sm:text-base">
                            Trabajas de lunes a domingo. En tu empleo, en la barbería, en el mostrador — como sea. Y el dinero no
                            te alcanza. Pasan los meses, pasan los años, y siempre estás en lo mismo.
                        </p>
                        <p className="mt-5 text-sm leading-relaxed text-[#999] sm:text-base">
                            Has visto videos de YouTube hasta cansarte. Has comprado cursitos de $50, de $100 que te dejaron solo
                            cuando algo falló. Has intentado dropshipping copiando productos random — y nadie te compró.
                        </p>
                    </Reveal>

                    <Reveal delay={180}>
                        <blockquote
                            className="my-10 border-l-2 border-[#ff5c00] pl-6 sm:pl-8"
                            data-testid="pain-quote"
                        >
                            <p className="serif-accent text-2xl leading-snug text-[#f5f5f5] sm:text-3xl">
                                “¿Será que esto no es pa' mí? ¿Será que yo no nací pa' esto?”
                            </p>
                        </blockquote>
                        <p className="text-sm leading-relaxed text-[#c9c9c9] sm:text-base">
                            Escúchame. No eres tú. Nadie nunca te enseñó el sistema correcto. Lo que consumiste es ruido — gurús
                            vendiéndote sueños que no aplican a tu país, tu situación, tu bolsillo.
                        </p>
                    </Reveal>
                </div>
            </section>

            <section className="relative overflow-hidden px-5 py-20 sm:py-28" data-testid="difference-section">
                <div className="relative mx-auto max-w-6xl">
                    <Reveal>
                        <div className="mb-4 flex items-center gap-2">
                            <span className="dot-blink h-1.5 w-1.5 rounded-full bg-[#22c55e]" />
                            <span className="text-xs font-medium uppercase tracking-[0.2em] text-[#888]">La diferencia real</span>
                        </div>
                        <h2 className="font-display max-w-2xl text-3xl font-extrabold leading-[1.08] sm:text-5xl">
                            No teoría de gringo. <em className="serif-accent text-grad">Lo que funciona en tu país.</em>
                        </h2>
                    </Reveal>

                    <Reveal delay={120}>
                        <div className="mt-8 max-w-2xl">
                            <p className="text-sm leading-relaxed text-[#999] sm:text-base">
                                Un cursito barato te deja tirado cuando algo falla. BIZecomacademy te acompaña hasta que generes
                                tu primera venta. Llamadas en vivo todos los lunes y jueves. Si algo falla, lo corregimos juntos.
                            </p>
                            <p className="mt-5 text-sm leading-relaxed text-[#999] sm:text-base">
                                Operamos con el modelo pago contra entrega (COD) — el cliente paga cuando recibe el producto en su
                                puerta. Sin tarjeta de crédito. Sin saturación gringa.
                            </p>
                        </div>
                    </Reveal>

                    <div className="mt-12 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                        {CARDS.map((card, i) => (
                            <Reveal key={card.title} delay={i * 90}>
                                <article
                                    className="card-hover flex h-full flex-col overflow-hidden rounded-2xl border border-[#222] bg-[#111111]"
                                    data-testid={`difference-card-${i + 1}`}
                                >
                                    <img
                                        src={card.img}
                                        alt={card.title}
                                        loading="lazy"
                                        className="aspect-video w-full object-cover"
                                    />
                                    <div className="flex flex-1 flex-col p-6">
                                    <span className="font-display text-grad text-3xl font-extrabold">{i + 1}</span>
                                    <h3 className="font-display mt-4 text-base font-bold leading-snug">{card.title}</h3>
                                    <p className="mt-3 flex-1 text-sm leading-relaxed text-[#999]">{card.desc}</p>
                                    </div>
                                </article>
                            </Reveal>
                        ))}
                    </div>

                    <Reveal delay={150} className="mt-12">
                        <div
                            className="grid grid-cols-2 divide-[#222] rounded-2xl border border-[#222] bg-[#111111] sm:grid-cols-4 sm:divide-x"
                            data-testid="stats-bar"
                        >
                            {STATS.map((s) => (
                                <div key={s.label} className="px-4 py-7 text-center">
                                    <p className="font-display text-grad text-3xl font-extrabold tracking-tight sm:text-4xl" data-testid={`stat-value-${s.label.toLowerCase().replace(/\s+/g, "-")}`}>
                                        <CountUp target={s.target} suffix={s.suffix} />
                                    </p>
                                    <p className="mt-1 text-xs font-medium uppercase tracking-[0.16em] text-[#888]">{s.label}</p>
                                </div>
                            ))}
                        </div>
                    </Reveal>
                </div>
            </section>
        </>
    );
}
